import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Tile from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import View from 'ol/View';
import Map from 'ol/Map';
import { fromLonLat } from 'ol/proj.js';
import { defaults as defaultControls } from 'ol/control';
import { logger } from '../../../../shared/utils/logger';
//import { getLength } from 'ol/sphere';
//import GeoJSON from 'ol/format/GeoJSON';

// Tipos para el servicio
export interface MapConfig {
  target: string;
  center: [number, number];
  zoom: number;
  defaultRefCat?: string;
}

export interface StoreyInfo {
  id: string;
  name: string;
  guid?: string;
}

export interface CategoryCounts {
  [category: string]: number;
}

export interface MapLayers {
  baseLayer: Tile;
  parcelaLayer: VectorLayer;
  bimLayer: VectorLayer;
  drawLayers: { [storeyId: string]: VectorLayer };
}

export interface AuxLayer {
  id: string;
  name: string;
  layer: VectorLayer;
  assignedStoreyIds: string[] | 'all';
}

export interface GUIController {
  map: Map;
  baseLayer: Tile;
  parcelaLayer: VectorLayer;
  bimLayer: VectorLayer;
  ifcModel: any;
  ifcObject: {
    ifcName: string;
    ifcFile: string;
    ifcElementsTree: any;
    ifcNumFloors: number;
    ifcSelectedStoreyId: string;
  };
  cadastralObject: {
    refCat: string;
  };
  lastRefCat: string | null;
  storeyChangeListeners: Array<(storeyId: string) => void>;
  
  onStoreyChange(callback: (storeyId: string) => void): void;
  notifyStoreyChange(storeyId: string): void;
  getStoreyDropdownOptions(): StoreyInfo[];
  selectStoreyById(storeyId: string): void;
  getCategoryCountsForStorey(storeyId: string): CategoryCounts;
  getSelectedStoreyId(): string;
  getStoreyHeight(storeyId: string): number;
  getIfcFileName(): string;
}

export class MapService {
  private map: Map | null = null;
  private gui: GUIController | null = null;
  private layers: MapLayers | null = null;
  private auxLayers: AuxLayer[] = [];
  private config: MapConfig;
  // Z-index constants to control layer stacking order
  private readonly Z_INDEX = {
    BASE: 0,
    PARCELA: 5,
    // AUX debe ir por debajo de BIM y por encima de PARCELA
    AUX: 8,
    BIM: 10,
    DRAW: 20,
  } as const;
  private isInitialized = false;
  public onElementsChanged: (() => void) | null = null;

  constructor(config: MapConfig) {
    this.config = config;
  }

  /**
   * Inicializa el mapa y todas sus capas
   */
  async initMap(): Promise<{ map: Map; gui: GUIController; layers: MapLayers }> {
    if (this.isInitialized) {
      throw new Error('MapService ya está inicializado');
    }

    logger.log('MAP_SERVICE', '🔧 MapService: Inicializando mapa...');
    
    // Verificar que el elemento del mapa existe
    const mapElement = document.getElementById(this.config.target);
    if (!mapElement) {
      throw new Error(`Elemento del mapa '${this.config.target}' no encontrado`);
    }
    
    logger.log('MAP_SERVICE', '🔧 MapService: Elemento del mapa encontrado:', mapElement);
    
    // Crear capas
    const layers = this.createLayers();
    this.layers = layers;
    
    try {
      // Crear el mapa
      this.map = new Map({
        target: this.config.target,
        layers: [layers.baseLayer, layers.parcelaLayer, layers.bimLayer],
        view: new View({
          center: fromLonLat(this.config.center),
          zoom: this.config.zoom
        }),
        controls: defaultControls({ zoom: false, rotate: false, attribution: false })
      });
      
      logger.log('MAP_SERVICE', '✅ MapService: Mapa creado exitosamente');
          } catch (error) {
        logger.error('MAP_SERVICE', '❌ MapService: Error al crear el mapa:', error);
        throw error;
      }

    // Habilitar cancelación con clic derecho
    this.enableRightClickCancel();

    // Inicializar GUI después de que el mapa esté listo
    await this.initializeGUI();
    
    this.isInitialized = true;
    
    return {
      map: this.map,
      gui: this.gui!,
      layers: this.layers
    };
  }

  /**
   * Crea las capas del mapa
   */
  private createLayers(): MapLayers {
    const baseLayer = new Tile({ source: new OSM() });

    const parcelaLayer = new VectorLayer({
      source: new VectorSource(),
      style: new Style({
        fill: new Fill({ color: 'rgba(0, 255, 0, 0.3)' }),
        stroke: new Stroke({ color: 'green', width: 2 })
      }),
      zIndex: this.Z_INDEX.PARCELA
    });

    const bimLayer = new VectorLayer({
      source: new VectorSource(),
      style: new Style({
        stroke: new Stroke({ color: 'black', width: 2 })
      }),
      zIndex: this.Z_INDEX.BIM
    });
    // Alinear el nombre para reutilización desde IFCService
    bimLayer.set('name', 'bim-layer');

    const drawLayers: { [storeyId: string]: VectorLayer } = {};

    return {
      baseLayer,
      parcelaLayer,
      bimLayer,
      drawLayers
    };
  }

  /**
   * Habilita la cancelación de dibujo con clic derecho
   */
  private enableRightClickCancel(): void {
    if (!this.map) return;

    this.map.getViewport().addEventListener("contextmenu", (event) => {
      event.preventDefault();
      // Esta funcionalidad se implementará cuando se migre el DrawingService
      logger.log('MAP_SERVICE', '🖱️ MapService: Clic derecho detectado (funcionalidad pendiente)');
    });
  }

  /**
   * Inicializa el GUIController
   */
  private async initializeGUI(): Promise<void> {
    if (!this.map || !this.layers) {
      throw new Error('Mapa o capas no inicializadas');
    }

    // Esperar a que el mapa esté completamente renderizado
    await new Promise<void>((resolve) => {
      this.map!.once('rendercomplete', () => resolve());
    });

    // Importar dinámicamente el modelo IFC
    const { ifcModel } = await import('../../classes/ifcModel');

    // Crear el GUIController
    this.gui = {
      map: this.map,
      baseLayer: this.layers.baseLayer,
      parcelaLayer: this.layers.parcelaLayer,
      bimLayer: this.layers.bimLayer,
      ifcModel: new ifcModel(),
      ifcObject: {
        ifcName: "",
        ifcFile: "data/small.ifc",
        ifcElementsTree: {},
        ifcNumFloors: 0,
        ifcSelectedStoreyId: "",
      },
      cadastralObject: {
        refCat: this.config.defaultRefCat || "",
      },
      lastRefCat: null,
      storeyChangeListeners: [],
      
      onStoreyChange(callback: (storeyId: string) => void) {
        this.storeyChangeListeners.push(callback);
      },
      
      notifyStoreyChange(storeyId: string) {
        this.storeyChangeListeners.forEach(callback => callback(storeyId));
      },
      
      getStoreyDropdownOptions(): StoreyInfo[] {
        const result: StoreyInfo[] = [];
        if (!this.ifcModel?.storeys) return result;
        
        // Usar GUIDs como identificadores principales
        const storeyGuids = this.ifcModel.getAllStoreyGuids();
        for (const guid of storeyGuids) {
          const storey = this.ifcModel.getStoreyByGuid(guid);
          if (storey) {
            const name = storey.Name?.value || `Planta ${guid}`;
            result.push({ id: guid, name, guid });
          }
        }
        return result;
      },
      
      selectStoreyById(storeyId: string) {
        // storeyId ahora es un GUID
        const storey = this.ifcModel?.getStoreyByGuid(storeyId);
        if (!storey) return;
        
        this.ifcObject.ifcSelectedStoreyId = storeyId;
        this.ifcModel.updadteSelectedStoreyIds([storeyId]);
        this.ifcModel.drawFilteredPlan();
        this.notifyStoreyChange(storeyId);
      },
      
      getCategoryCountsForStorey(storeyId: string): CategoryCounts {
        const tree = this.ifcModel.getGeoElementsTreeXZ();
        const counts: CategoryCounts = {};
        
        if (!tree[storeyId]) return counts;
        
        for (const category of Object.keys(tree[storeyId])) {
          const elements = tree[storeyId][category];
          counts[category] = Object.keys(elements).length;
        }
        
        return counts;
      },
      
      getSelectedStoreyId(): string {
        return this.ifcObject.ifcSelectedStoreyId;
      },
      
      getStoreyHeight(storeyId: string): number {
        if (!this.ifcModel) return 0;
        // storeyId ahora es un GUID, pero mantenemos compatibilidad con expressID
        return this.ifcModel.storeysElevations[storeyId] || 0;
      },
      
      getIfcFileName(): string {
        if (this.ifcObject.ifcName && this.ifcObject.ifcName.trim() !== '') {
          return this.ifcObject.ifcName;
        } else if (this.ifcObject.ifcFile && this.ifcObject.ifcFile !== 'data/small.ifc') {
          const filePath = this.ifcObject.ifcFile.split('/');
          return filePath[filePath.length - 1].replace('.ifc', '');
        }
        return '';
      }
    };

    logger.log('MAP_SERVICE', '✅ MapService: GUIController inicializado');
    
    // Configurar listener para manejar visibilidad de capas de dibujo cuando cambia la planta
    this.gui.onStoreyChange((storeyId: string) => {
              logger.log('MAP_SERVICE', `🔄 MapService: Cambio de planta detectado: ${storeyId}`);
      
      // Ocultar todas las capas de dibujo
      if (this.layers?.drawLayers) {
        Object.values(this.layers.drawLayers).forEach(layer => {
          layer.setVisible(false);
        });
        
        // Mostrar solo la capa de la planta seleccionada
        if (storeyId && this.layers.drawLayers[storeyId]) {
          this.layers.drawLayers[storeyId].setVisible(true);
          logger.log('MAP_SERVICE', `✅ MapService: Capa de planta ${storeyId} visible`);
        }
      }
    });
    
    // Cargar estado de visibilidad desde storage al inicializar
    this.loadStoreyVisibilityFromStorage();

    // Cargar capas auxiliares persistidas
    this.loadAuxLayersFromStorage();
  }

  /**
   * Actualiza el modelo IFC en el GUIController
   */
  public updateIfcModel(ifcModelInstance: any): void {
    if (this.gui) {
      logger.log('MAP_SERVICE', '🔄 MapService: Actualizando ifcModel en GUIController');
      this.gui.ifcModel = ifcModelInstance;
      logger.log('MAP_SERVICE', '✅ MapService: ifcModel actualizado, storeys:', ifcModelInstance.storeys ? Object.keys(ifcModelInstance.storeys).length : 'No storeys');
      
      // Mostrar lista de plantas y sus nombres cuando se carga un IFC
      if (ifcModelInstance.storeys && Object.keys(ifcModelInstance.storeys).length > 0) {
        logger.log('MAP_SERVICE', '🏗️ MAP_SERVICE: === LISTA DE PLANTAS DEL MODELO IFC ===');
        const storeyOptions = this.gui.getStoreyDropdownOptions();
        storeyOptions.forEach((storey, index) => {
          logger.log('MAP_SERVICE', `   ${index + 1}. ID: ${storey.id} | Nombre: ${storey.name}`);
        });
        logger.log('MAP_SERVICE', '🏗️ MAP_SERVICE: ===========================================');
      } else {
        logger.log('MAP_SERVICE', '⚠️ MAP_SERVICE: No se encontraron plantas en el modelo IFC');
      }
      
      // No hacer selección automática para evitar interferir con el renderizado
      // La selección se manejará desde el hook useIFC
    } else {
      logger.warn('MAP_SERVICE', '⚠️ MapService: GUI no inicializado, no se puede actualizar ifcModel');
    }
  }

  /**
   * Obtiene la capa activa de dibujo (de la planta seleccionada)
   */
  getActiveDrawLayer(): VectorLayer | null {
    if (!this.gui) return null;
    const activeStoreyId = this.gui.getSelectedStoreyId();
    return activeStoreyId ? this.getOrCreateDrawLayer(activeStoreyId) : null;
  }

  /**
   * Carga el estado de visibilidad de las plantas desde localStorage
   */
  loadStoreyVisibilityFromStorage(): void {
    try {
      const stored = sessionStorage.getItem('ifcStoreyVisibility');
      if (stored) {
        const storeyVisibility = JSON.parse(stored);
        logger.log('MAP_SERVICE', '📁 MapService: Estado de visibilidad cargado:', storeyVisibility);
        
        // Aplicar el estado de visibilidad a las capas existentes
        if (this.layers?.drawLayers) {
          Object.entries(storeyVisibility).forEach(([storeyId, visible]) => {
            const drawLayers = this.layers!.drawLayers;
            const layer = drawLayers[storeyId];
            if (layer && typeof visible === 'boolean') {
              layer.setVisible(visible);
              logger.log('MAP_SERVICE', `🔄 MapService: Capa ${storeyId} visibilidad establecida a: ${visible}`);
            }
          });
        }
      }
          } catch (error) {
        logger.error('MAP_SERVICE', '❌ MapService: Error al cargar estado de visibilidad:', error);
      }
  }

  /**
   * Obtiene o crea una capa de dibujo para una planta específica
   */
  getOrCreateDrawLayer(storeyId: string): VectorLayer | null {
    if (!this.layers || !storeyId) {
      logger.warn('MAP_SERVICE', '⚠️ MapService: getOrCreateDrawLayer: storeyId es null o no hay capas');
      return null;
    }
    
          logger.log('MAP_SERVICE', `🔧 MapService: Verificando capa para planta ${storeyId}`);
    
    if (!this.layers.drawLayers[storeyId]) {
              logger.log('MAP_SERVICE', `🏗️ MapService: Creando nueva capa para planta ${storeyId}`);
      const isActiveStorey = this.gui && this.gui.getSelectedStoreyId() === storeyId;
              logger.log('MAP_SERVICE', `📊 MapService: ¿Es planta activa? ${isActiveStorey}`);
      
      this.layers.drawLayers[storeyId] = new VectorLayer({
        source: new VectorSource(),
        style: this.createFeatureStyleFunction(),
        visible: isActiveStorey ?? false,
        zIndex: this.Z_INDEX.DRAW
      });
      
      // Set the name property after creation
      this.layers.drawLayers[storeyId].set('name', `draw-layer-${storeyId}`);
      
      if (this.map) {
        this.map.addLayer(this.layers.drawLayers[storeyId]);
      }
      
              logger.log('MAP_SERVICE', `✅ MapService: Capa creada y añadida al mapa para planta ${storeyId}`);
          } else {
        logger.log('MAP_SERVICE', `📋 MapService: Capa ya existe para planta ${storeyId}`);
      }
    
    return this.layers.drawLayers[storeyId];
  }



  /**
   * Obtiene el mapa
   */
  getMap(): Map | null {
    return this.map;
  }

  /**
   * Obtiene el GUIController
   */
  getGUI(): GUIController | null {
    return this.gui;
  }

  /**
   * Obtiene las capas
   */
  getLayers(): MapLayers | null {
    return this.layers;
  }

  /**
   * Obtiene los datos de elementos dibujados de todas las capas
   * @deprecated Usar el store unificado en su lugar
   */
  getElementsData(): any[] {
    if (!this.layers) return [];

    const elements: any[] = [];
    
    Object.entries(this.layers.drawLayers).forEach(([storeyId, layer]) => {
      const source = layer.getSource();
      if (!source) return;
      
      const features = source.getFeatures();
      
      features.forEach((feature, index) => {
        const geometry = feature.getGeometry();
        if (geometry) {
          // Usar la geometría original para evitar interferir con la visualización
          const originalGeometry = feature.getGeometry();
          
          const element = {
            id: `${storeyId}-${index}`,
            storeyId: storeyId,
            category: feature.get('type') || 'desconocido',
            area: originalGeometry.getArea(),
            finca: feature.get('finca') || null,
            computo: feature.get('computo') || null,
            nombre: feature.get('nombre') || null,
            geometry: originalGeometry,
            feature: feature
          };
          
          elements.push(element);
        }
      });
    });
    
    return elements;
  }

  /**
   * Obtiene los datos de elementos usando el store unificado
   */
  getElementsDataFromUnifiedStore(): any[] {
    try {
      // Importar dinámicamente para evitar dependencias circulares
      const { useUnifiedElementsStore } = require('../../stores/unifiedElementsStore');
      const store = useUnifiedElementsStore.getState();
      return store.elements;
    } catch (error) {
      logger.warn('MAP_SERVICE', '⚠️ MapService: No se pudo obtener datos del store unificado:', error);
      // Fallback al método original
      return this.getElementsData();
    }
  }

  /**
   * Verifica si el servicio está inicializado
   */
  isReady(): boolean {
    return this.isInitialized && this.map !== null && this.gui !== null;
  }

  /**
   * Crea una función de estilo para las features de dibujo
   */
  private createFeatureStyleFunction() {
    return (feature: any) => {
      const type = feature.get('type');
      logger.log('MAP_SERVICE', `🎨 MapService: Aplicando estilo para categoría: ${type}`);
      
      // Usar un color por defecto
      let color = 'rgba(128, 128, 128, 0.7)';
      
      // Intentar obtener el color del store de categorías
      try {
        // Usar una referencia global al store que se puede establecer desde el componente
        if ((window as any).__categoriesStore) {
          const getCategoryColor = (window as any).__categoriesStore.getCategoryColor;
          if (getCategoryColor) {
            color = getCategoryColor(type) || color;
            logger.log('MAP_SERVICE', `🎨 MapService: Color obtenido para categoría ${type}:`, color);
          }
        }
      } catch (error) {
        logger.warn('MAP_SERVICE', 'No se pudo obtener el color del store de categorías:', error);
      }
      
      return new Style({
        fill: new Fill({ color }),
        stroke: new Stroke({ color: 'black', width: 2 })
      });
    };
  }

  /**
   * AUXILIARY LAYERS API
   * Capas vectoriales entre PARCELA y BIM. Asignables a una o varias plantas, o a todas.
   */
  addEmptyAuxLayer(name: string, assignedStoreyIds: string[] | 'all' = 'all'): AuxLayer {
    const source = new VectorSource();
    const layer = new VectorLayer({
      source,
      zIndex: this.Z_INDEX.AUX,
      visible: true
    });
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const aux: AuxLayer = { id, name, layer, assignedStoreyIds };
    this.auxLayers.push(aux);
    if (this.map) this.map.addLayer(layer);
    this.updateAuxLayersVisibility();
    this.persistAuxLayersToStorage();
    logger.log('MAP_SERVICE', `🧩 MapService: Capa auxiliar vacía creada: ${name} (id=${id})`);
    return aux;
  }

  addAuxLayerFromGeoJSON(name: string, geojson: any, assignedStoreyIds: string[] | 'all' = 'all'): AuxLayer {
    // Cargar GeoJSON dinámicamente para evitar peso inicial
    const source = new VectorSource();
    const layer = new VectorLayer({
      source,
      zIndex: this.Z_INDEX.AUX,
      visible: true
    });
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const aux: AuxLayer = { id, name, layer, assignedStoreyIds };

    // Parseo perezoso del GeoJSON (import dinámico)
    import('ol/format/GeoJSON').then(({ default: GeoJSON }) => {
      try {
        const fmt = new GeoJSON();
        const feats = fmt.readFeatures(geojson, { featureProjection: 'EPSG:3857' });
        // Add features to the source
        source.addFeatures(feats);
      } catch (e) {
        logger.error('MAP_SERVICE', '❌ MapService: Error cargando GeoJSON en capa auxiliar:', e);
      }
    });

    this.auxLayers.push(aux);
    if (this.map) {
      // Insertar por debajo de BIM y por encima de PARCELA: zIndex ya lo asegura
      this.map.addLayer(layer);
    }
    this.updateAuxLayersVisibility();
    logger.log('MAP_SERVICE', `🧩 MapService: Capa auxiliar añadida: ${name} (id=${id})`);
    return aux;
  }

  setAuxLayerAssignment(auxId: string, assignedStoreyIds: string[] | 'all'): void {
    const aux = this.auxLayers.find(a => a.id === auxId);
    if (!aux) return;
    aux.assignedStoreyIds = assignedStoreyIds;
    this.updateAuxLayersVisibility();
    this.persistAuxLayersToStorage();
    logger.log('MAP_SERVICE', `🧩 MapService: Asignación actualizada para ${aux.name}`, assignedStoreyIds);
  }

  removeAuxLayer(auxId: string): void {
    const idx = this.auxLayers.findIndex(a => a.id === auxId);
    if (idx === -1) return;
    const [aux] = this.auxLayers.splice(idx, 1);
    if (this.map) this.map.removeLayer(aux.layer);
    this.persistAuxLayersToStorage();
    logger.log('MAP_SERVICE', `🗑️ MapService: Capa auxiliar eliminada: ${aux.name}`);
  }

  clearAuxLayers(): void {
    this.auxLayers.forEach(a => this.map && this.map.removeLayer(a.layer));
    this.auxLayers = [];
    this.persistAuxLayersToStorage();
    logger.log('MAP_SERVICE', '🗑️ MapService: Todas las capas auxiliares eliminadas');
  }

  listAuxLayers(): AuxLayer[] {
    return [...this.auxLayers];
  }

  private updateAuxLayersVisibility(): void {
    if (!this.gui) return;
    const active = this.gui.getSelectedStoreyId();
    this.auxLayers.forEach(a => {
      const visible = a.assignedStoreyIds === 'all' || (active && Array.isArray(a.assignedStoreyIds) && a.assignedStoreyIds.includes(active));
      a.layer.setVisible(Boolean(visible));
    });
  }

  private persistAuxLayersToStorage(): void {
    try {
      const meta = this.auxLayers.map(a => ({
        id: a.id,
        name: a.name,
        assignedStoreyIds: a.assignedStoreyIds
      }));
      sessionStorage.setItem('auxLayers', JSON.stringify(meta));
      logger.log('MAP_SERVICE', '💾 MapService: Capas auxiliares persistidas');
    } catch (e) {
      logger.warn('MAP_SERVICE', '⚠️ MapService: Error persistiendo capas auxiliares:', e);
    }
  }

  private loadAuxLayersFromStorage(): void {
    try {
      const raw = sessionStorage.getItem('auxLayers');
      if (!raw) return;
      const meta = JSON.parse(raw) as Array<{ id: string; name: string; assignedStoreyIds: string[] | 'all' }>;
      meta.forEach(m => {
        // recrear capa vacía con mismo nombre y asignación; mantener nuevo id
        const aux = this.addEmptyAuxLayer(m.name, m.assignedStoreyIds);
        // opcional: respetar id anterior para estabilidad
        aux.id = m.id as any;
      });
      // Repersistir con ids actuales
      this.persistAuxLayersToStorage();
      logger.log('MAP_SERVICE', `📁 MapService: Capas auxiliares cargadas (${meta.length})`);
    } catch (e) {
      logger.warn('MAP_SERVICE', '⚠️ MapService: Error cargando capas auxiliares:', e);
    }
  }

  /**
   * Muestra una etiqueta sobre un elemento del mapa
   */
  showElementLabel(feature: any, text: string): void {
    if (!this.map) return;
    
    try {
      // Limpiar cualquier etiqueta anterior primero
      this.hideElementLabel();
      
      // Crear un overlay para la etiqueta
      const geometry = feature.getGeometry();
      if (!geometry) return;
      
      const extent = geometry.getExtent();
      const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
      
      // Crear el elemento HTML de la etiqueta
      const labelElement = document.createElement('div');
      labelElement.className = 'v2-element-label';
      labelElement.textContent = text;
      labelElement.style.cssText = `
        position: absolute;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        z-index: 1000;
        pointer-events: none;
        transform: translate(-50%, -100%);
        margin-top: -8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      `;
      
      // Crear el overlay usando import dinámico
      import('ol/Overlay').then(({ default: Overlay }) => {
        // Verificar que el mapa aún existe antes de añadir el overlay
        if (!this.map) return;
        
        const overlay = new Overlay({
          element: labelElement,
          positioning: 'bottom-center',
          offset: [0, -10],
          stopEvent: false
        });
        
        // Posicionar el overlay en el centro del elemento
        overlay.setPosition(center);
        
        // Añadir al mapa
        this.map.addOverlay(overlay);
        
        // Guardar referencia para poder eliminarlo después
        (this as any).currentLabelOverlay = overlay;
        
        logger.log('MAP_SERVICE', `🏷️ MapService: Etiqueta mostrada para elemento: ${text}`);
              }).catch(error => {
          logger.error('MAP_SERVICE', 'Error al importar Overlay:', error);
        });
      
    } catch (error) {
              logger.error('MAP_SERVICE', 'Error al mostrar etiqueta:', error);
    }
  }

  /**
   * Oculta la etiqueta actual del mapa
   */
  hideElementLabel(): void {
    if (!this.map) {
      logger.log('MAP_SERVICE', '🏷️ MapService: No hay mapa, no se puede ocultar etiqueta');
      return;
    }
    
    try {
      // Verificar si hay overlay actual
      const currentOverlay = (this as any).currentLabelOverlay;
      if (currentOverlay) {
        logger.log('MAP_SERVICE', '🏷️ MapService: Eliminando overlay actual');
        this.map.removeOverlay(currentOverlay);
        (this as any).currentLabelOverlay = null;
        logger.log('MAP_SERVICE', '🏷️ MapService: Etiqueta ocultada correctamente');
              } else {
          logger.log('MAP_SERVICE', '🏷️ MapService: No hay overlay actual para ocultar');
        }
    } catch (error) {
              logger.error('MAP_SERVICE', 'Error al ocultar etiqueta:', error);
      // Limpiar referencia incluso si hay error
      (this as any).currentLabelOverlay = null;
    }
  }

  /**
   * Refresca los estilos de todas las features en las capas de dibujo
   */
  async refreshDrawLayerStyles(): Promise<void> {
    if (!this.layers) return;
    
    try {
      // Importar dinámicamente el store para evitar dependencias circulares
      const categoriesModule = await import('../../stores/categoriesStore');
      const { getCategoryColor } = categoriesModule.useCategoriesStore.getState();
      
      Object.entries(this.layers.drawLayers).forEach(([_storeyId, layer]) => {
        const features = layer.getSource()?.getFeatures();
        if (features) {
          features.forEach(feature => {
            const type = feature.get('type');
            logger.log('MAP_SERVICE', `🎨 MapService: Refrescando estilo para feature tipo: ${type}`);
            
            // Obtener el color del store de categorías
            const color = getCategoryColor(type) || 'rgba(128, 128, 128, 0.7)';
            logger.log('MAP_SERVICE', `🎨 MapService: Color refrescado para categoría ${type}:`, color);
            
            // Aplicar el nuevo estilo
            feature.setStyle(new Style({
              fill: new Fill({ color }),
              stroke: new Stroke({ color: 'black', width: 2 })
            }));
          });
        }
      });
      
      logger.log('MAP_SERVICE', '✅ MapService: Estilos de features refrescados');
    } catch (error) {
      logger.warn('MAP_SERVICE', 'No se pudo obtener el color del store de categorías:', error);
    }
  }

  /**
   * Destruye el mapa y limpia recursos
   */
  destroy(): void {
    if (this.map) {
      this.map.setTarget(undefined);
      this.map = null;
    }
    this.gui = null;
    this.layers = null;
    this.isInitialized = false;
    logger.log('MAP_SERVICE', '🗑️ MapService: Recursos liberados');
  }
}

// Exportar una instancia singleton (opcional)
let mapServiceInstance: MapService | null = null;

export function createMapService(config: MapConfig): MapService {
  if (mapServiceInstance) {
    mapServiceInstance.destroy();
  }
  mapServiceInstance = new MapService(config);
  return mapServiceInstance;
}

export function getMapService(): MapService | null {
  return mapServiceInstance;
}
